import { Deployment, DeploymentHistory, ChatResponse, Config, ECSJob, AppHealthcheckMapping } from './types';

// API base URL - configure via environment variable or use relative path
const API_BASE = import.meta.env.VITE_API_URL || '/api';

export async function fetchDeployments(env?: string): Promise<Deployment[]> {
  console.log('API_BASE:', API_BASE);
  const url = env
    ? `${API_BASE}/deployments/${encodeURIComponent(env)}`
    : `${API_BASE}/deployments`;

  console.log('url:', url);

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch deployments: ${response.statusText}`);
  }

  const data = await response.json();
  return data.deployments;
}

export async function fetchHistory(envApp: string): Promise<DeploymentHistory[]> {
  const url = `${API_BASE}/history/${encodeURIComponent(envApp)}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch history: ${response.statusText}`);
  }

  const data = await response.json();
  return data.history;
}

export async function sendChatMessage(
  message: string,
  sessionId?: string
): Promise<ChatResponse> {
  const response = await fetch(`${API_BASE}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      message,
      sessionId,
    }),
  });

  if (!response.ok) {
    throw new Error(`Chat request failed: ${response.statusText}`);
  }

  return response.json();
}

export async function deleteSession(sessionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/chat/sessions/${sessionId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete session: ${response.statusText}`);
  }
}

export async function fetchConfig(): Promise<Config> {
  const url = `${API_BASE}/config`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch config: ${response.statusText}`);
  }

  return response.json();
}

export async function updateAppMappings(
  mappings: Record<string, string>
): Promise<void> {
  const response = await fetch(`${API_BASE}/config/app_type_mappings`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ mappings }),
  });

  if (!response.ok) {
    throw new Error(`Failed to update app mappings: ${response.statusText}`);
  }
}

export async function saveAppHealthcheckMappings(
  mappings: AppHealthcheckMapping[]
): Promise<void> {
  const response = await fetch(`${API_BASE}/config/app_healthcheck_mappings`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ mappings }),
  });

  if (!response.ok) {
    throw new Error(`Failed to save healthcheck mappings: ${response.statusText}`);
  }
}

export interface CodeDeployStatusResponse {
  message: string;
  env: string;
  app: string;
  codeDeployId: string;
  codeDeployState: string;
  deploymentStatus: string;
}

export async function checkCodeDeployStatus(
  env: string,
  app: string
): Promise<CodeDeployStatusResponse> {
  const response = await fetch(`${API_BASE}/codedeploy-status/check`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ env, app }),
  });

  if (!response.ok) {
    throw new Error(`CodeDeploy status check failed: ${response.statusText}`);
  }

  return response.json();
}

export interface CodeDeployJobResponse {
  deploymentId: string;
  applicationName: string;
  deploymentGroupName: string;
  status: string;
  errorCode: string;
  errorMessage: string;
  createTime: string;
  completeTime: string;
  computePlatform: string;
  isFailed: boolean;
  summary: string;
  consoleUrl?: string;
  targets: Array<{
    targetId: string;
    targetArn: string;
    targetType: string;
    status: string;
    lifecycleEvents: Array<{
      eventName: string;
      status: string;
      startTime: string;
      endTime: string;
      diagnosticsMessage: string;
      diagnosticsScriptName: string;
      diagnosticsLogTail: string;
      errorCode: string;
    }>;
    failedEvent: {
      eventName: string;
      status: string;
      startTime: string;
      endTime: string;
      diagnosticsMessage: string;
      diagnosticsScriptName: string;
      diagnosticsLogTail: string;
      errorCode: string;
    } | null;
  }>;
}

export async function checkCodeDeployJobDetails(
  deploymentId: string,
  account: string
): Promise<CodeDeployJobResponse> {
  const response = await fetch(`${API_BASE}/codedeploy-job/check`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ deploymentId, account }),
  });

  if (!response.ok) {
    throw new Error(`CodeDeploy job check failed: ${response.statusText}`);
  }

  return response.json();
}

export interface EcsDeployStatusResponse {
  message: string;
  env: string;
  app: string;
  ecsCluster: string;
  ecsService: string;
  deploymentStatus: string;
  ecsDeploymentId?: string;
  taskDefinition?: string;
  ecrImage?: string;
  rolloutState?: string;
  rolloutStateReason?: string;
  taskCounts?: {
    desired: number;
    running: number;
    pending: number;
  };
}

export async function checkEcsDeployStatus(
  env: string,
  app: string
): Promise<EcsDeployStatusResponse> {
  const response = await fetch(`${API_BASE}/ecsdeploy-status/check`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ env, app }),
  });

  if (!response.ok) {
    throw new Error(`ECS deploy status check failed: ${response.statusText}`);
  }

  return response.json();
}

export interface EcsServiceCheckResponse {
  cluster: string;
  service: string;
  deploymentId: string;
  rolloutState: string;
  rolloutStateReason?: string;
  failedTasksCount: number;
  desiredCount: number;
  runningCount: number;
  pendingCount: number;
  taskDefinition: { name: string; revision: string; full: string };
  isFailed: boolean;
  stoppedTasks: Array<{
    taskId: string;
    stopCode: string;
    stoppedReason: string;
    stoppedAt: string;
    containerFailures: Array<{ name: string; exitCode: number; reason: string }>;
  }>;
  recentEvents: Array<{ timestamp: string; message: string }>;
  summary: string;
  consoleUrl: string;
}

export async function checkEcsServiceDetails(
  env: string,
  app: string
): Promise<EcsServiceCheckResponse> {
  const response = await fetch(`${API_BASE}/ecs-service/check`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ env, app }),
  });

  if (!response.ok) {
    throw new Error(`ECS service check failed: ${response.statusText}`);
  }

  return response.json();
}

export interface HealthCheckResponse {
  env: string;
  app: string;
  healthStatus: string | null;
  checkTime?: number;
  checkTimeISO?: string;
  healthcheck_type?: string;
  message?: string;
  dispatchedTo?: string;
  targetLambda?: string;
  error?: { code: string; message: string };
}

export async function checkHealthStatus(
  env: string,
  app: string
): Promise<HealthCheckResponse> {
  const response = await fetch(`${API_BASE}/healthcheck/check`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ env, app }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error?.message || `Health check failed: ${response.statusText}`);
  }

  return response.json();
}

// ECS Jobs mock data for development
export function getMockECSJobs(): ECSJob[] {
  const environments = ['QA', 'UA', 'PROD', 'DEV'];
  const apps = ['DataSync', 'ReportGenerator', 'CacheWarmer', 'BackupJob', 'CleanupTask', 'NotificationSender'];
  const results = ['Success', 'Failed', 'Running', 'Pending', 'Unknown'];
  const accounts = ['123456789012', '234567890123'];

  const jobs: ECSJob[] = [];

  environments.forEach(env => {
    apps.forEach(app => {
      jobs.push({
        Account: accounts[Math.floor(Math.random() * accounts.length)],
        Env: env,
        App: app,
        Ver: `1.${Math.floor(Math.random() * 10)}.${Math.floor(Math.random() * 100)}`,
        LastRun: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
        Result: results[Math.floor(Math.random() * results.length)],
        ECSCluster: `${env.toLowerCase()}-cluster`,
        ECSService: `${env.toLowerCase()}-${app.toLowerCase()}-job`,
      });
    });
  });

  return jobs;
}

export async function fetchECSJobs(): Promise<ECSJob[]> {
  // TODO: Replace with actual API call when backend is ready
  // const url = `${API_BASE}/ecs-jobs`;
  // const response = await fetch(url);
  // if (!response.ok) {
  //   throw new Error(`Failed to fetch ECS jobs: ${response.statusText}`);
  // }
  // const data = await response.json();
  // return data.jobs;

  // For now, return mock data
  return getMockECSJobs();
}
